import { InputAdornment, TextField } from '@mui/material';
import { makeStyles } from '@mui/styles';
import SearchIcon from '@mui/icons-material/Search';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { AppContext } from '../../../../RoutesWithAuth';
import axios from '../../../common/AxiosConfig';
//import { horizontalTabStyles } from '../sharedStyles';
import Pagination from '../grid/Pagination';
import PrivacyTaskDetails from './privacytasks/PrivacyTaskDetails';
// Task fields eligible for the comma-separated search box
const SEARCHABLE_KEYS = ['task_id', 'title', 'task_owner_name', 'status', 'priority', 'stage'];
// Columns whose long text wraps instead of stretching the table
const WRAPPED_COLUMN_KEYS = ['title', 'poc_name', 'task_owner_name'];
const PAGE_SIZE = 25;
const DEFAULT_COLUMNS = [
  { key: "task_id", label: "Task ID", link: true },
  { key: "title", label: "Privacy Task Title", link: false },
  { key: "poc_name", label: "First and Last Name", link: false },
  { key: "task_owner_name", label: "Task Owner", link: false },
  { key: "priority", label: "Priority", link: false },
  { key: "status", label: "Status", link: false },
  { key: "created_date", label: "Created Date", link: false },
  { key: "due_date", label: "Due date", link: false }
];

// Custom page Level Styling
const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    flexDirection: 'row',
    height: '100%',
    width: '100%',
    paddingTop: '10px',
    //background: '#f0f4f8',
    '& .linkNotavailable': {
      color: 'red'
    },

  },
  tabBody: {
    //...horizontalTabStyles.tabBody,
    //border: '1px solid #6591b696',
    //boxShadow: '0px 0px 1px 0px rgb(0 0 0 / 41%)',
    background: '#fff',
    borderRadius: '10px',
    padding: '10px',
    width: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    //margin: '0 16px',
  },
  searchWrapper: {
    marginBottom: '12px',
    maxWidth: '420px',
    '& .MuiInputBase-input::placeholder': {
      fontSize: '0.8rem'
    }
  },
  taskLink: {
    padding: 0,
    border: 'none',
    background: 'transparent',
    color: '#007bff',
    cursor: 'pointer',
    font: 'inherit',
    textDecoration: 'underline'
  },
  tableWrapper: {
    width: '101%',
    //borderRadius: '10px',
    overflowX: 'auto',
    overflowY: 'auto',
    maxHeight: 'calc(100vh - 194.5px)',
    //border: '1px solid #e2e8f0',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    // Thin scrollbar matching the CPS cgrid table scroll style
    '&::-webkit-scrollbar': {
      width: '8px',
      height: '10px'
    },
    '&::-webkit-scrollbar-track': {
      background: '#f1f1f1'
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#a8a8a8',
      borderRadius: '4px',
      '&:hover': {
        background: '#888'
      }
    },
    '& table': {
      width: '100%',
      minWidth: 'max-content',
      borderCollapse: 'separate',
    },
    '& thead': {
      position: 'sticky',
      top: 0,
      zIndex: 1,
      background: '#8CC0E0',
    },
    '& th': {
      padding: '7px 16px',
      //textAlign: 'center',
      color: '#202124',
      fontWeight: 600,
      fontSize: '0.85rem',
      borderRight: '1px solid #c8cbce',
      letterSpacing: '0.3px',
      whiteSpace: 'nowrap',
      background: '#8CC0E0',
      position: 'relative',
      '&:last-child': {
        borderRight: 'none',
      },
    },
    '& tbody tr': {
      height: '6vh',
      background: '#f7fafd',
      borderBottom: '1px solid #edf2f7',
      transition: 'background-color 0.2s ease',
      //background: '#dee2e6',
      '&:hover': {
        background: '#f0f7ff',
      },
    },
    '& td': {
      padding: '7px 16px',
      textAlign: 'left',
      color: '#334155',
      fontSize: '0.85rem',
      borderRight: '1px solid #edf2f7',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      '&:last-child': {
        borderRight: 'none',
      },
    },
    '& th.wrapCell, & td.wrapCell': {
      whiteSpace: 'normal',
      maxWidth: '165px',
      overflowWrap: 'anywhere',
    },
  },
  paginationWrapper: {
    marginTop: '12px',
    '& nav': {
      display: 'block'
    },
    '& .pagination': {
      display: 'flex',
      alignItems: 'center',
      margin: 0,
      padding: 0,
      listStyle: 'none'
    },
    '& ul.pagination': {
      justifyContent: 'flex-end',
      gap: '8px'
    }
  }
}));

const PrivacyTasks = ({ tabActiveParam }) => {
  const classes = useStyles();
  const context = useContext(AppContext);
  const authToken = context.authToken.get;
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [columns, setColumns] = useState(DEFAULT_COLUMNS);
  const [tasks, setTasks] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: PAGE_SIZE,
    total: 0,
    total_pages: 1
  });
  const [isInProgress, setIsInProgress] = useState(false);
  const [error, setError] = useState('');
  const [selectedTask, setSelectedTask] = useState(null);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken, currentPage]);

  // Splits the comma-separated search text into individual lowercased terms
  const searchTerms = useMemo(() => (
    searchTerm.split(',').map((term) => term.trim().toLowerCase()).filter(Boolean)
  ), [searchTerm]);

  const filteredTasks = useMemo(() => {
    if (searchTerms.length === 0) {
      return tasks;
    }
    return tasks.filter((task) => searchTerms.some((term) => (
      SEARCHABLE_KEYS.some((key) => String(task[key] || '').toLowerCase().includes(term))
    )));
  }, [searchTerms, tasks]);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  const pageTasks = filteredTasks;

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const renderCell = (task, column) => {
    const value = task[column.key] || '';
    if (column.link) {
      return (
        <button className={classes.taskLink} type="button" onClick={() => setSelectedTask(task)}>
          {value}
        </button>
      );
    }
    return value;
  };

  const fetchData = () => {
    setIsInProgress(true);
    setError('');
    axios.get('centra/privacy-tasks', {
      headers: {
        Authorization: authToken
      },
      params: {
        page: currentPage,
        page_size: PAGE_SIZE
      }
    }).then((res) => {
      const responseData = res.data || {};
      setColumns(responseData.columns?.length ? responseData.columns : DEFAULT_COLUMNS);
      setTasks(responseData.tasks || []);
      setPagination({
        page: responseData.pagination?.page || currentPage,
        page_size: responseData.pagination?.page_size || PAGE_SIZE,
        total: responseData.pagination?.total || 0,
        total_pages: responseData.pagination?.total_pages || 1
      });
    }).catch((err) => {
      setColumns(DEFAULT_COLUMNS);
      setTasks([]);
      setPagination({
        page: currentPage,
        page_size: PAGE_SIZE,
        total: 0,
        total_pages: 1
      });
      setError(err?.response?.data?.message || err?.message || 'Unable to load privacy tasks.');
    }).finally(() => {
      setIsInProgress(false);
    });
  }

  if (selectedTask) {
    return (
      <div className={classes.root}>
        <PrivacyTaskDetails task={selectedTask} onBack={() => setSelectedTask(null)} />
      </div>
    );
  }

  return (
    <div className={classes.root}>
      {/* {isInProgress && (<>
                <LoadingIndicator />
            </>)}
            */}
      <div className={classes.tabBody}>
        <div className={classes.searchWrapper}>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            placeholder="Search Tasks by Task ID, Title, Owner, Status..."
            //sx={{"& input::placeholder": { textAlign: "left", paddingLeft: "30px" } }}
            value={searchTerm}
            onChange={handleSearchChange}
            data-testid="privacy-tasks-search"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              )
            }}
          />
        </div>
        <div className={classes.tableWrapper}>
          <table>
            <thead>
              <tr>
                {columns.map((column) => (
                  <th key={column.key} className={WRAPPED_COLUMN_KEYS.includes(column.key) ? 'wrapCell' : undefined}>
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isInProgress && (
                <tr>
                  <td colSpan={columns.length}>Loading privacy tasks...</td>
                </tr>
              )}
              {!isInProgress && error && (
                <tr>
                  <td colSpan={columns.length}>{error}</td>
                </tr>
              )}
              {!isInProgress && !error && pageTasks.length === 0 && (
                <tr>
                  <td colSpan={columns.length}>No privacy tasks found.</td>
                </tr>
              )}
              {!isInProgress && !error && pageTasks.map((task) => (
                <tr key={task.task_id}>
                  {columns.map((column) => <td key={column.key} className={WRAPPED_COLUMN_KEYS.includes(column.key) ? 'wrapCell' : undefined}>{renderCell(task, column)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {pagination.total > pagination.page_size && (
          <div className={classes.paginationWrapper}>
            <Pagination
              currentPage={currentPage}
              pageCount={pagination.page_size}
              totalResults={pagination.total}
              pageChange={handlePageChange}
            />
          </div>
        )}
      </div>
    </div>
  )
};

export default React.memo(PrivacyTasks);